import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import ButtonPrimary from '../../components/Buttons/Button';
import Link from '../../components/Link';
import Modal from '../../components/Modal';
import SaveAndCloseModal from '../../components/Modals/SaveAndCloseModal/SaveAndCloseModal';
import OnBoardingContainer from '../../components/OnBoarding/OnBoardingContainer';
import { ParagraphNormalSub } from '../../components/Paragraph';
import SelectFundingMethod from '../../components/SelectFundingMethod/SelectFundingMethod';
import {
  ACCOUNT_TYPE_GTM,
  SIGNIFYD_SESSION_ID,
} from '../../constants/sessionStorage';

import useApplicationFundingData from '../../hooks/useApplicationFundingData';
import useApplicationUid from '../../hooks/useApplicationUid';
import useBankApplicationFundingData from '../../hooks/useBankApplicationFundingData';
import { useMenu } from '../../hooks/useMenu';
import useStepPost from '../../hooks/useStepPost';
import loadSignifydDeviceFingerprintScript from '../../signifydDeviceFingerprint';
import { deleteApplicationFunding } from '../../store/actions/funding/fundingActions';
import { selectSettings } from '../../store/selectors/settingsSelectors';
import {
  retrieveFromSessionStorage,
  storeInSessionStorage,
} from '../../util/helpers/sessionStorageHelper';
import { gtmStepHelper } from '../../util/helpers/gtmHelper';
import useGtmStepHook from '../../util/hooks/useGtmStepHook';
import { ACCOUNT_STEP_COMPLETE, ACCOUNT_STEP_VIEW } from '../../util/gtmEvents';
import { useApplicantData } from '../../hooks/useApplicantData';

const ParagraphModal = styled(ParagraphNormalSub)`
  text-align: center;
`;

const ButtonContainer = styled.div`
  display: grid;
  grid-template-columns: 2fr;
  grid-gap: 1.5rem;
  margin-top: 1rem;

  @media only screen and (max-width: 750px) {
    margin-top: auto;
    display: grid;
    grid-template-columns: 1fr;
    grid-gap: 1rem;
  }
`;

const FundAccount = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { activeMenu, activeItem } = useMenu();
  const [isSkipOpen, setSkipOpen] = useState(false);
  const ApplicantData = useApplicantData();
  const { ApplicationUid } = useApplicationUid();
  const { ApplicationFunding } = useApplicationFundingData();
  const { BankApplicationFunding } = useBankApplicationFundingData();
  const { BrokerName } = useSelector(selectSettings);
  const [eventType, setEventType] = useState('NoForm');
  const [saveAndCloseModalOpen, setSaveAndCloseModalOpen] = useState(false);
  const { gtmStepEvent } = useGtmStepHook();
  const accountGtmType = retrieveFromSessionStorage(ACCOUNT_TYPE_GTM);

  useEffect(() => {
    gtmStepHelper({
      gtmStepEvent,
      eventName: ACCOUNT_STEP_VIEW,
      stepLabel: 'Fund your account',
      accountType: accountGtmType,
      accountName: ApplicantData?.PersonName,
      accountDateOfBirth: ApplicantData?.PersonBirthday,
      accountPhone: ApplicantData?.Phone,
      accountAddress: ApplicantData?.PersonAddress,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGtmEvent = () => {
    gtmStepHelper({
      gtmStepEvent,
      eventName: ACCOUNT_STEP_COMPLETE,
      stepLabel: 'Fund your account - Skip funding',
      accountType: accountGtmType,
      accountName: ApplicantData?.PersonName,
      accountDateOfBirth: ApplicantData?.PersonBirthday,
      accountPhone: ApplicantData?.Phone,
      accountAddress: ApplicantData?.PersonAddress,
    });
  };

  useEffect(() => {
    loadSignifydDeviceFingerprintScript();
    const script = document.getElementById('sig-api');
    storeInSessionStorage(SIGNIFYD_SESSION_ID, script.dataset.orderSessionId);
  }, []);

  const { onSkipStep } = useStepPost({
    nextPath: activeItem?.nextPath,
    skipStepPath: activeItem?.skipPath,
  });

  const onSkipCall = () => {
    if (ApplicationFunding || BankApplicationFunding) {
      dispatch(
        deleteApplicationFunding({
          ApplicationUid,
          onSuccess: onSkipStep,
        }),
      );
      handleGtmEvent();
    } else {
      onSkipStep();
      handleGtmEvent();
    }
  };

  return (
    <OnBoardingContainer
      activeMenu={activeMenu}
      activeItem={activeItem}
      previousPath={activeItem?.previousPath}
      onSkip={() => setSkipOpen(true)}
      setEventType={setEventType}
      eventType={eventType}
      setSaveAndCloseModalOpen={setSaveAndCloseModalOpen}
    >
      <Helmet>
        <title>
          {t('fundAccount.titleTag', {
            brokerName: BrokerName,
          })}
        </title>
      </Helmet>
      <SelectFundingMethod nextPath={activeItem?.nextPath} />
      <Modal
        isOpen={isSkipOpen}
        onClose={() => setSkipOpen(false)}
        label={t('fundAccount.skipModalTitle')}
        isSmall
        center
      >
        <ParagraphModal>{t('fundAccount.skipModalSubtitle')}</ParagraphModal>
        <ButtonContainer>
          <ButtonPrimary
            title={t('fundAccount.skipSkip')}
            onClick={() => setSkipOpen(false)}
            data-cy="button-cancel-skip-funding"
          />
          <Link
            onClick={onSkipCall}
            title={t('fundAccount.SKIP')}
            data-cy="button-confirm-skip-funding"
          />
        </ButtonContainer>
      </Modal>
      {saveAndCloseModalOpen && (
        <SaveAndCloseModal
          isOpen={saveAndCloseModalOpen}
          onClose={() => setSaveAndCloseModalOpen(false)}
        />
      )}
    </OnBoardingContainer>
  );
};

export default FundAccount;
